import { CircularProgress } from '@mui/material';
import React, {
    ChangeEvent,
    Dispatch,
    MouseEvent,
    ReactNode,
    SetStateAction,
    forwardRef,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useReducer,
    useRef,
    useState
} from 'react';

import { SelectedLocationContext } from 'contexts/selected-location';
import { Page, Role } from 'types';
import { Filter, FilterOperator, FilterState, LogicalOperator, filtersToRsqlString } from 'utils';

import { useDialog } from 'components/organization/dialog/dialog';
import { Popover } from 'components/organization/popover/popover';
import { useAuth } from 'contexts';
import { useDebounce } from 'hooks';
import useTimeZone from 'hooks/use-timezone';
import { useUserAPI } from 'hooks/use-user-api';
import { TableType } from 'types/data-table';
import { buildCsv } from 'utils/csv';
import { SavedFilterPreset, UserSavedFilters } from 'types/saved-filter';
import { ColumnsControl } from './controls/columns-control';
import { FiltersControl } from './controls/filters-control';
import { SaveFilterDialog } from './controls/save-filter-dialog';
import { ConfirmDeletePresetDialog } from './controls/confirm-delete-preset-dialog';
import './data-table.scss';

export type DataTableColumn = {
  field: string;
  sortField?: string;
  label: string;
  width?: number;
  type?: string;
  headerClass?: string;
  className?: string;
  filterable?: boolean;
  sortable?: boolean;
  hidden?: boolean /* for custom filters -- never displayed nor exported */;
  valueOptions?: any[];
  classCondition?: (value: any, row?: any) => boolean;
  getValue?: (row?: any, col?: any) => any;
  getValueLabel?: (value: any, row?: any) => string | undefined | null;
  renderCell?: (value: any, row?: any) => ReactNode;
  // whether the column should be skipped for quick search. undefined is considered false.
  skipQuickSearch?: boolean;
  allowedRoles?: Role[];
  // table preferences refactor, display the columns categorized when toggling them.
  category?: string;
  dateFormat?: string;
};

export type FetchParams = {
  pageNum?: number;
  pageSize?: number;
  from?: Date;
  to?: Date;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  filterString?: string;
  quickSearch?: string;
};

const fieldNameMap: Record<string, string> = {
  booking: 'booking.bookingNumber',
  location: 'location.name',
  truckingCompany: 'truckingCompany.name'
};

export interface DataTableRef {
  forceFetch: () => void;
  setFilterState: Dispatch<SetStateAction<FilterState>>;
}

interface DataTableProps {
  fetchData: (params: FetchParams) => Promise<Page<any> | any[] | null>;
  fetchCsv?: (
    columns: string[],
    filterString?: string,
    sortField?: string,
    sortDirection?: string,
    headers?: string[]
  ) => Promise<void>;
  // CSV filename for client-side CSV export
  csvFilename?: string;
  columns: DataTableColumn[];
  initialColumns?: { field: string; width?: number }[];
  initialPage?: FetchParams;
  initialFilters?: Partial<FilterState>;
  pageSizeOptions?: number[];
  filterByLocation?: boolean;
  // whether pagination, filtering, and sorting are handled client-side
  clientSidePagination?: boolean;
  onCellClick?: (field: string, value: any, row?: any) => void;
  onRowClick?: (row: any) => void;
  // for checkboxes
  checkboxSelection?: boolean;
  onRowSelectionModelChange?: (rows: any[]) => void;
  // row actions
  getRowActions?: (row: any) => JSX.Element[];
  tableClass?: string;
  hideTableControls?: boolean;
  rowClassName?: (row: any) => string;
  noRowsMessage?: React.ReactNode;
  enableSaveColumns?: TableType;
  showColumnsCategorized?: boolean; // for columns popover
  columnCategories?: string[];
  ['data-cy']?: string;
  customQuickSearch?: (input: string) => string;
  // optional content rendered immediately to the left of the search input
  searchAdornment?: ReactNode;
  onPresetApplied?: (preset: SavedFilterPreset | null) => void;
}

const DataTable = forwardRef<DataTableRef, DataTableProps>(
  ({ fetchData, customQuickSearch, showColumnsCategorized = true, ...props }, dataTableRef) => {
    const [data, setData] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number>();
    const [loading, setLoading] = useState(false);
    const [fetchParams, setFetchParams] = useState<FetchParams>({
      ...props.initialPage,
      pageNum: props.initialPage?.pageNum || 0,
      pageSize: props.initialPage?.pageSize || 100
    });
    const [visibleFields, setVisibleFields] = useState<{ field: string; width?: number }[]>(
      props.initialColumns?.map(c => {
        return { field: c.field, width: c.width ?? 80 };
      }) ?? []
    );
    // Column states
    const { userProfile } = useAuth();
    const [columns, setColumns] = useState<DataTableColumn[]>(
      props.columns.filter(({ allowedRoles }) => !allowedRoles || allowedRoles.includes(userProfile.role))
    );
    const [columnSearch, setColumnSearch] = useState('');
    const visibleColumns = useMemo(() => {
      const matchedColumns: DataTableColumn[] = [];
      for (let i = 0; i < visibleFields.length; i++) {
        const col = columns.find(c => c.field === visibleFields[i].field);
        if (!!col) {
          matchedColumns.push({ ...col, width: visibleFields[i].width });
        }
      }
      return matchedColumns;
    }, [columns, visibleFields]);

    // Filter
    const [filters, setFilters] = useState<Filter[]>(props.initialFilters?.filters ?? []);
    const [logicalOperator, setLogicalOperator] = useState(props.initialFilters?.logicalOperator ?? LogicalOperator.AND);
    // indices of selected rows
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    // keep track of last successful request
    const lastRequestTime = useRef(0);
    // loading variable for export to CSV
    const [loadingCSV, setLoadingCSV] = useState(false);
    const [, forceUpdate] = useReducer(x => x + 1, 0);

    // states for columns dialog
    const [showAllCategories, setShowAllCategories] = useState(false);

    const { selectedLocation } = useContext(SelectedLocationContext);
    const {
      timeZoneIndicator,
      dateInputToUtc,
      dateTimeLocalToUtc,
      newZonedDayJs,
      utcToZonedDate,
      utcToZonedDateTimeLocal
    } = useTimeZone();

    const handleFetchData = useCallback(
      (params: FetchParams, startTime: number) => {
        fetchData(params).then(res => {
          // if this is not the latest request, ignore it
          if (startTime === lastRequestTime.current) {
            if (Array.isArray(res)) {
              setData(res);
              setTotalCount(res.length);
            } else {
              setData(res?.content ?? []);
              setTotalCount(res?.totalElements);
            }
            setLoading(false);
          }
        });
        setSelectedRows([]);
        if (!!props.onRowSelectionModelChange) {
          props?.onRowSelectionModelChange([]);
        }
      },
      [fetchData]
    );

    const { debounce: debouncedFetch } = useDebounce(handleFetchData);

    const handleFetch = useCallback(() => {
      setLoading(true);
      lastRequestTime.current = performance.now();
      const startTime = lastRequestTime.current;
      debouncedFetch(fetchParams, startTime);
    }, [fetchParams, debouncedFetch]);

    useEffect(handleFetch, [handleFetch]);

    useEffect(forceUpdate, [data]);

    const setFilterState = useCallback(
      (setStateAction: SetStateAction<FilterState>) => {
        if (typeof setStateAction === 'function') {
          setFilters(filters => setStateAction({ filters, logicalOperator: LogicalOperator.AND }).filters);
          setLogicalOperator(logicalOperator => setStateAction({ filters: [], logicalOperator }).logicalOperator);
        } else {
          setFilters(setStateAction.filters);
          setLogicalOperator(setStateAction.logicalOperator);
        }
      },
      [setFilters, setLogicalOperator]
    );

    /* allow parent components to force fetch via ref */
    /* TODO: implement changing filters (or just initial filters, e.g. transaction date range) */
    useImperativeHandle(dataTableRef, () => ({ forceFetch: handleFetch, setFilterState }), [
      handleFetch,
      setFilterState
    ]);

    // Methods for client-side Pagination, filtering, sorting, respectively
    const getDataPage = useCallback(
      (input: any[]) => {
        if (!props.clientSidePagination) {
          return input;
        } else {
          const pageNum = fetchParams.pageNum ?? 0;
          const pageSize = fetchParams.pageSize ?? 100;
          return input.slice(pageNum * pageSize, Math.min((pageNum + 1) * pageSize, input.length));
        }
      },
      [fetchParams.pageNum, fetchParams.pageSize]
    );

    // if the data is not paginated server-side, filter locally
    const filterData = useCallback(
      (rows: any[]) => {
        if (props.clientSidePagination && (filters.length !== 0 || !!fetchParams.quickSearch)) {
          const temp = [...rows].filter(r => {
            return filterRow(r);
          });
          return temp;
        }
        return rows;
      },
      [filters, logicalOperator, fetchParams.quickSearch]
    );

    // if the data is not paginated server-side, sort locally
    const sortData = useCallback(
      (rows: any[]) => {
        if (props.clientSidePagination && !!fetchParams.sortField && !!fetchParams.sortDirection) {
          const temp = [...rows];
          return temp.sort((a, b) => {
            // @ts-ignore
            const valueA = a[fetchParams.sortField];
            // @ts-ignore
            const valueB = b[fetchParams.sortField];
            if (valueA === valueB) {
              return 0;
            }
            const gt = valueA > valueB;

            return (fetchParams.sortDirection === 'desc' && gt) || (fetchParams.sortDirection === 'asc' && !gt)
              ? -1
              : 1;
          });
        }
        return rows;
      },
      [fetchParams.sortField, fetchParams.sortDirection]
    );

    // helper method to check if a row should be filtered (for client-side processing)
    const filterRow = (row: any) => {
      // check if the row should be filtered by quick search
      if (!!fetchParams.quickSearch) {
        if (
          !visibleColumns.some(c => {
            if (!fetchParams.quickSearch) {
              return false;
            }
            const val = getColumnValueLabel(row, c);
            if (!val) {
              return false;
            }
            return val.toLowerCase().includes(fetchParams.quickSearch.trim().toLowerCase());
          })
        ) {
          return false;
        } else if (filters.length === 0) {
          return true;
        }
      }
      // what to change the rowAllowed variable to if a condition is met
      const booleanChange = logicalOperator === LogicalOperator.AND ? false : true;
      for (let i = 0; i < filters.length; i++) {
        const f = filters[i];
        // find the column this references
        const col = columns.find(col => col.field === f.field);
        if (!col) {
          continue;
        }
        // get the displayed value on the table
        let value = getColumnValue(row, col);
        // if the value is empty, skip
        if (value === undefined || value === null || (value === '' && f.value !== '')) {
          continue;
        }

        // for LogicalOperator.AND, return if any comparison is FALSE
        // for LogicalOperator.OR, return if any comparison is TRUE
        const isOr = logicalOperator === LogicalOperator.OR;
        let matches: boolean;

        switch (f.filterOperator) {
          case FilterOperator.CONTAINS:
            // TODO: may need to expand this depending on what types are possible
            if (!value.includes) {
              value = value.toString();
            }
            matches = value.includes(f.value);
            if (matches && isOr) {
              return true;
            } else if (!matches && !isOr) {
              return false;
            }
            break;
          case FilterOperator.STARTS_WITH:
            if (!value.startsWith) {
              value = value.toString();
            }
            matches = value.startsWith(f.value);
            if (matches && isOr) {
              return true;
            } else if (!matches && !isOr) {
              return false;
            }
            break;
          case FilterOperator.ENDS_WITH:
            if (!value.endsWith) {
              value = value.toString();
            }
            matches = value.endsWith(f.value);
            if (matches && isOr) {
              return true;
            } else if (!matches && !isOr) {
              return false;
            }
            break;
          case FilterOperator.NOT:
          case FilterOperator.NOT_EQUALS:
            matches = value !== f.value;
            if (matches && isOr) {
              return true;
            } else if (!matches && !isOr) {
              return false;
            }
            break;
          case FilterOperator.AFTER:
          case FilterOperator.GREATER_THAN:
            matches = value > f.value;
            if (matches && isOr) {
              return true;
            } else if (!matches && !isOr) {
              return false;
            }
            break;
          case FilterOperator.ON_OR_AFTER:
          case FilterOperator.GREATER_THAN_OR_EQUALS:
            matches = value >= f.value;
            if (matches && isOr) {
              return true;
            } else if (!matches && !isOr) {
              return false;
            }
            break;
          case FilterOperator.BEFORE:
          case FilterOperator.LESS_THAN:
            matches = value < f.value;
            if (matches && isOr) {
              return true;
            } else if (!matches && !isOr) {
              return false;
            }
            break;
          case FilterOperator.ON_OR_BEFORE:
          case FilterOperator.LESS_THAN_OR_EQUALS:
            matches = value <= f.value;
            if (matches && isOr) {
              return true;
            } else if (!matches && !isOr) {
              return false;
            }
            break;
          case FilterOperator.IS:
          case FilterOperator.EQUALS:
          default:
            // Default to just using equal comparison
            matches = value === f.value;
            if (matches && isOr) {
              return true;
            } else if (!matches && !isOr) {
              return false;
            }
            break;
        }
      }
      // if nothing matched, return TRUE for AND, or FALSE for OR
      return !booleanChange;
    };

    // helper method to combine client-side processing
    const getData = useCallback(() => {
      return !props.clientSidePagination ? data : getDataPage(sortData(filterData(data)));
    }, [getDataPage, sortData, filterData, data]);

    // update filter string based on location
    useEffect(() => {
      let filterString = filtersToRsqlString(
        {
          logicalOperator,
          filters
        },
        fieldNameMap
      );
      if (selectedLocation && props.filterByLocation && !!selectedLocation.id) {
        const locationFilter = `location.id==${selectedLocation.id}`;
        filterString = !filterString ? locationFilter : `${filterString};${locationFilter}`;
      }
      // update filter string based on quickSearch
      if (!!fetchParams.quickSearch) {
        const quickSearchFilterString =
          customQuickSearch?.(fetchParams.quickSearch) ??
          `(${[
            ...new Set(
              visibleColumns
                // filter out columns that explicitly skipQuickSearch
                // TODO: temp fix to filter out boolean columns because TRUE values all when anything is entered into quick search
                .filter(c => !c.skipQuickSearch && c.type !== 'boolean')
                .map(c => {
                  const fieldName = fieldNameMap[c.field] ?? c.field;
                  let filter = `${fieldName}=="*${fetchParams.quickSearch?.trim()}*"`;
                  return filter;
                })
            )
          ].join(',')})`;
        filterString = !filterString ? quickSearchFilterString : `${filterString};${quickSearchFilterString}`;
      }
      setFetchParams({ ...fetchParams, filterString });
    }, [
      props.filterByLocation,
      selectedLocation,
      logicalOperator,
      filters,
      fetchParams.quickSearch,
      customQuickSearch
    ]);

    // End Checkbox Methods
    const handleUpdateRowSelection = (idx: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked !== selectedRows.includes(idx)) {
        const currRows = [...selectedRows];
        const currData = getData();
        // findIndex will always return >= 0, since selectedRows.includes === true
        event.target.checked
          ? currRows.push(idx)
          : currRows.splice(
              currRows.findIndex(n => n === idx),
              1
            );
        setSelectedRows(currRows);
        if (!!props.onRowSelectionModelChange) {
          props?.onRowSelectionModelChange(currRows.map(idx => currData[idx]));
        }
      }
      // else, do nothing
    };
    // End Checkbox Methods

    // column methods
    useEffect(() => {
      setColumns(props.columns.filter(({ allowedRoles }) => !allowedRoles || allowedRoles.includes(userProfile.role)));
    }, [props.columns, userProfile.role]);

    const handleEditColumn = (col: DataTableColumn) => {
      const currVisibleFields = [...visibleFields];
      const i = visibleFields.findIndex(f => f.field === col.field);
      if (i > -1) {
        currVisibleFields[i] = { field: col.field, width: col.width };
        setVisibleFields(currVisibleFields);
      }
    };

    const handleSort = (field: string) => (event: React.MouseEvent<unknown>) => {
      const direction = fetchParams.sortField === field && fetchParams.sortDirection === 'asc' ? 'desc' : 'asc';
      setFetchParams({
        ...fetchParams,
        sortDirection: direction,
        sortField: field
      });
    };

    const getColumnValue = (row: any, col: DataTableColumn) => {
      return !!col.getValue ? col.getValue(row, col) : row[col.field];
    };

    const getColumnValueLabel = (row: any, col: DataTableColumn) => {
      const value = getColumnValue(row, col);
      // convert value to string if it exists, otherwise use undefined
      /* MODIFIED (2024-10-03): allow false, 0 */
      return !!col.getValueLabel ? col.getValueLabel(value, row) : (value ?? null) !== null ? String(value) : undefined;
    };

    // Column UI methods
    const handleColumnResize =
      (col: DataTableColumn) => (event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>) => {
        event.nativeEvent.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();
        const startWidth = col.width ?? 80;
        const startX = event.pageX;
        const onMouseMove = (event: globalThis.MouseEvent) => {
          event.stopImmediatePropagation();
          handleEditColumn({ ...col, width: Math.max(startWidth - startX + event.pageX, 30) });
        };
        const onMouseUp = (event: globalThis.MouseEvent) => {
          event.stopImmediatePropagation();
          document.body.removeEventListener('mousemove', onMouseMove);
        };
        document.body.addEventListener('mousemove', onMouseMove);
        document.body.addEventListener('mouseup', onMouseUp, { once: true });
      };

    const handleColumnDrag = (col: DataTableColumn) => (event: React.DragEvent<HTMLDivElement>) => {
      if (!document.elementsFromPoint(event.clientX, event.clientY).some(e => e.classList.contains('drag-handle'))) {
        return event.preventDefault();
      }
      const startX = event.pageX;
      // get all 'td' elements in 'thead' => get all x positions
      const tableHeaders = Array.from(document.getElementsByTagName('thead')[0].querySelectorAll('td'));
      let headerPositions = tableHeaders.map(e => e.getBoundingClientRect().x);
      // find which column index we are interacting with
      headerPositions.findIndex(x => x > startX);
      event.dataTransfer.setData('text', `col-${visibleFields.findIndex(f => f.field === col.field)}`);
    };

    const allowDrop = (event: React.DragEvent<any>) => {
      event.preventDefault();
      // else, do nothing
    };

    const handleColumnDrop = (event: React.DragEvent<HTMLTableElement>) => {
      if (!event.dataTransfer.getData('text').startsWith('col-')) {
        return;
      }
      const origInd = +event.dataTransfer.getData('text').substring(4);
      // get all 'td' elements in 'thead' => get all x positions
      const headerPositions = Array.from(document.getElementsByTagName('thead')[0].querySelectorAll('td')).map(
        e => e.getBoundingClientRect().x
      );
      // Find the last index where the header position is < current mouse pageX. Default to 0.
      let dropInd = 0;
      for (let i = headerPositions.length - 1; i >= 0; i--) {
        if (headerPositions[i] < event.pageX) {
          dropInd = i;
          break;
        }
      }
      if (origInd !== dropInd) {
        const fields = [...visibleFields];
        const temp = fields.splice(origInd, 1)[0];
        fields.splice(dropInd, 0, temp);
        setVisibleFields(fields);
      }
    };

    // End Column Methods

    const handleAddFilter = () => {
      setFilters([
        ...filters,
        {
          field: columns[0].field,
          value: '',
          filterOperator: FilterOperator.CONTAINS
        }
      ]);
    };

    const handleRemoveFilter = (index: number) => {
      if (index >= 0 && index < filters.length) {
        const temp = [...filters];
        temp.splice(index, 1);
        setFilters(temp);
      }
    };

    const getFilterOperators = (filter: Filter, index: number) => {
      const col = columns.find(c => c.field === filter.field);
      switch (col?.type) {
        case 'dateTime':
        case 'date':
          return (
            <select
              name="operator"
              value={filter.filterOperator}
              onChange={event =>
                handleFilterUpdate({ ...filter, filterOperator: event.target.value as FilterOperator }, index)
              }
              data-cy="filter-operator-date-time-select"
            >
              <option value={FilterOperator.IS}>is</option>
              <option value={FilterOperator.AFTER}>is after</option>
              <option value={FilterOperator.ON_OR_AFTER}>is on or after</option>
              <option value={FilterOperator.BEFORE}>is before</option>
              <option value={FilterOperator.ON_OR_BEFORE}>is on or before</option>
            </select>
          );
        case 'boolean':
          return (
            <select
              name="operator"
              value={filter.filterOperator}
              disabled
              onChange={event =>
                handleFilterUpdate({ ...filter, filterOperator: event.target.value as FilterOperator }, index)
              }
              data-cy="filter-operator-boolean-select"
            >
              <option value={FilterOperator.EQUALS}>is</option>
            </select>
          );
        case 'number':
          return (
            <select
              name="operator"
              value={filter.filterOperator}
              onChange={event =>
                handleFilterUpdate({ ...filter, filterOperator: event.target.value as FilterOperator }, index)
              }
              data-cy="filter-operator-number-select"
            >
              <option value={FilterOperator.EQUALS}>=</option>
              <option value={FilterOperator.NOT_EQUALS}>!=</option>
              <option value={FilterOperator.GREATER_THAN}>{'>'}</option>
              <option value={FilterOperator.GREATER_THAN_OR_EQUALS}>{'>='}</option>
              <option value={FilterOperator.LESS_THAN}>{'<'}</option>
              <option value={FilterOperator.LESS_THAN_OR_EQUALS}>{'<='}</option>
            </select>
          );
        case 'singleSelect':
          return (
            <select
              name="operator"
              value={filter.filterOperator}
              onChange={event =>
                handleFilterUpdate({ ...filter, filterOperator: event.target.value as FilterOperator }, index)
              }
              data-cy="filter-operator-single-select"
            >
              <option value={FilterOperator.IS}>is</option>
              <option value={FilterOperator.NOT}>is not</option>
            </select>
          );
        case 'role':
          return (
            <select
              name="operator"
              value={filter.filterOperator}
              onChange={event =>
                handleFilterUpdate({ ...filter, filterOperator: event.target.value as FilterOperator }, index)
              }
              data-cy="filter-operator-role-select"
            >
              <option value={FilterOperator.EQUALS}>is</option>
              <option value={FilterOperator.NOT_EQUALS}>is not</option>
            </select>
          );
        default:
          return (
            <select
              name="operator"
              value={filter.filterOperator}
              onChange={event =>
                handleFilterUpdate({ ...filter, filterOperator: event.target.value as FilterOperator }, index)
              }
              data-cy="filter-operator-default-select"
            >
              <option value={FilterOperator.CONTAINS}>contains</option>
              <option value={FilterOperator.EQUALS}>equals</option>
              <option value={FilterOperator.STARTS_WITH}>starts with</option>
              <option value={FilterOperator.ENDS_WITH}>ends with</option>
            </select>
          );
      }
    };

    const getFilterValueComponent = (filter: Filter, index: number) => {
      const col = columns.find(c => c.field === filter.field);
      switch (col?.type) {
        case 'dateTime':
          return (
            <input
              className="flex-fill"
              type="datetime-local"
              value={utcToZonedDateTimeLocal(filter.value)}
              onChange={event =>
                handleFilterUpdate({ ...filter, value: dateTimeLocalToUtc(event.target.value) }, index)
              }
              data-cy="filter-value-date-time-input"
            />
          );
        case 'date':
          return (
            <input
              className="flex-fill"
              type="date"
              value={utcToZonedDate(filter.value)}
              onChange={event => handleFilterUpdate({ ...filter, value: dateInputToUtc(event.target.value) }, index)}
              data-cy="filter-value-date-input"
            />
          );
        case 'boolean':
          return (
            <select
              className="flex-fill"
              value={filter.value}
              onChange={event => handleFilterUpdate({ ...filter, value: event.target.value }, index)}
              data-cy="filter-value-boolean-input"
            >
              <option value={'true'}>{!!col.getValueLabel ? col.getValueLabel(true) : 'true'}</option>
              <option value={'false'}>{!!col.getValueLabel ? col.getValueLabel(false) : 'false'}</option>
            </select>
          );
        case 'number':
          return (
            <input
              className="flex-fill"
              type="number"
              value={filter.value}
              onChange={event => handleFilterUpdate({ ...filter, value: event.target.value }, index)}
              data-cy="filter-value-number-input"
            />
          );
        case 'singleSelect':
          return (
            <select
              className="flex-fill"
              value={filter.value}
              onChange={event => handleFilterUpdate({ ...filter, value: event.target.value }, index)}
              data-cy="filter-value-single-select"
            >
              {!!col.valueOptions && col.valueOptions.map(opt => <option value={opt}>{opt}</option>)}
            </select>
          );
        case 'role':
          return (
            <select
              className="flex-fill"
              value={filter.value}
              onChange={event => handleFilterUpdate({ ...filter, value: event.target.value }, index)}
              data-cy="filter-value-role-select"
            >
              <option value={Role.NONE}>NONE</option>
              <option value={Role.ADMIN}>ADMIN</option>
              <option value={Role.BILLING_ADMIN}>BILLING ADMIN</option>
              <option value={Role.SENIOR_BOOKING}>BOOKING - SENIOR</option>
              <option value={Role.BOOKING}>BOOKING</option>
              <option value={Role.CARRIER}>CARRIER</option>
              <option value={Role.ADMIN_CUSTOMER}>ADMIN CUSTOMER</option>
              <option value={Role.CUSTOMER}>CUSTOMER</option>
              <option value={Role.EXTERNAL}>EXTERNAL</option>
              <option value={Role.GATE}>GATE</option>
              <option value={Role.GATE_BOOKING}>GATE - BOOKING</option>
            </select>
          );
        default:
          return (
            <input
              className="flex-fill"
              type="text"
              value={filter.value}
              onChange={event => handleFilterUpdate({ ...filter, value: event.target.value }, index)}
              data-cy="filter-value-default-input"
            />
          );
      }
    };

    const handleFilterUpdate = (filter: Filter, index: number) => {
      const currFilters = [...filters];
      // if changing fields, reset other attributes
      if (currFilters[index].field !== filter.field) {
        const col = columns.find(c => c.field === filter.field);
        switch (col?.type) {
          case 'dateTime':
            currFilters[index] = { ...filter, filterOperator: FilterOperator.IS, value: '' };
            break;
          case 'boolean':
            currFilters[index] = { ...filter, filterOperator: FilterOperator.IS, value: 'true' };
            break;
          default:
            currFilters[index] = { ...filter, filterOperator: FilterOperator.CONTAINS, value: '' };
        }
      } else {
        currFilters[index] = filter;
      }
      setFilters(currFilters);
    };
    // End Filter Methods

    // CSV Export Methods
    const handleExportToCSV = () => {
      setLoadingCSV(true);
      // if the data is paginated server-side, and a fetchCsv() method has been provided, call the method.
      if (!props.clientSidePagination && !!props.fetchCsv) {
        return props
          .fetchCsv(
            visibleColumns.filter(c => !c.hidden).map(c => c.field),
            fetchParams.filterString,
            fetchParams.sortField,
            fetchParams.sortDirection,
            visibleColumns.filter(c => !c.hidden).map(c => c.label)
          )
          .then(() => setLoadingCSV(false));
      }
      const cols = visibleColumns;
      const csvData = buildCsv([
        cols.map(c => c.label),
        ...data.map(r => cols.map(c => getColumnValueLabel(r, c)))
      ]);

      // download csv data ----------------------------------
      // create file
      const file = new Blob([csvData], { type: 'text/csv' });
      // create temporary link to file
      const tempLink = document.createElement('a');
      tempLink.download = props.csvFilename ?? 'ITI intermodal.csv';
      const url = window.URL.createObjectURL(file);
      tempLink.href = url;
      // hide, show, and click link
      tempLink.style.display = 'none';
      document.body.appendChild(tempLink);
      tempLink.click();
      // cleanup
      document.body.removeChild(tempLink);
      setLoadingCSV(false);
    };
    // End CSV Export Methods

    // Column Helper Methods
    const { updateUserSavedFilters } = useUserAPI();

    // Saved Filters logic
    const savedFiltersMap = useMemo(() => {
      try {
        if (userProfile.savedFiltersJson) {
          let parsed = JSON.parse(userProfile.savedFiltersJson);
          // Handle the case where the JSON was double-stringified in the DB
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
          }
          if (typeof parsed === 'object' && parsed !== null) {
            return parsed as UserSavedFilters;
          }
        }
      } catch (e) {
        console.error(e);
      }
      return {} as UserSavedFilters;
    }, [userProfile.savedFiltersJson]);

    const locationKey = useMemo(() => {
      if (props.enableSaveColumns) {
        return selectedLocation ? `${props.enableSaveColumns}_${selectedLocation.id}` : props.enableSaveColumns;
      }
      return '';
    }, [props.enableSaveColumns, selectedLocation]);

    const presetsForTable: SavedFilterPreset[] = useMemo(() => {
      if (locationKey) {
        return savedFiltersMap[locationKey] || [];
      }
      return [];
    }, [savedFiltersMap, locationKey]);

    const [activePresetId, setActivePresetId] = useState<string>('');

    // Sync active preset from localStorage when switching locations
    useEffect(() => {
      if (locationKey) {
        setActivePresetId(localStorage.getItem(`lastPreset_${locationKey}`) || '');
      } else {
        setActivePresetId('');
      }
    }, [locationKey]);

    const saveFilterDialog = useDialog();
    const deleteFilterDialog = useDialog();
    const savedFiltersPopoverRef = useRef<any>(null);

    // Auto-load on mount or when presets change/location switches
    useEffect(() => {
      if (locationKey) {
        const preset = presetsForTable.find(p => p.id === activePresetId);
        if (preset) {
          setFilters(preset.filters);
          setLogicalOperator(preset.logicalOperator ?? LogicalOperator.AND);
          setVisibleFields(preset.visibleFields.map(field => {
            const pref = userProfile.columnPreferences.find(c => c.table === props.enableSaveColumns && c.column === field);
            return { field, width: pref?.width ?? 80 };
          }));
          props.onPresetApplied?.(preset);
        } else {
          // Reset to defaults if switching to a location without an active preset
          setFilters(props.initialFilters?.filters ?? []);
          setLogicalOperator(props.initialFilters?.logicalOperator ?? LogicalOperator.AND);
          setVisibleFields(
            props.initialColumns?.map(c => {
              return { field: c.field, width: c.width ?? 80 };
            }) ?? []
          );
          props.onPresetApplied?.(null);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [presetsForTable, activePresetId, locationKey]);

    const handleApplyPreset = (presetId: string) => {
      if (!locationKey) return;
      setActivePresetId(presetId);
      localStorage.setItem(`lastPreset_${locationKey}`, presetId);
      if (presetId) {
        const preset = presetsForTable.find(p => p.id === presetId);
        if (preset) {
          setFilters(preset.filters);
          setLogicalOperator(preset.logicalOperator ?? LogicalOperator.AND);
          setVisibleFields(preset.visibleFields.map(field => {
            const pref = userProfile.columnPreferences.find(c => c.table === props.enableSaveColumns && c.column === field);
            return { field, width: pref?.width ?? 80 };
          }));
          props.onPresetApplied?.(preset);
        }
      } else {
        setFilters(props.initialFilters?.filters ?? []);
        setLogicalOperator(props.initialFilters?.logicalOperator ?? LogicalOperator.AND);
        setVisibleFields(
          props.initialColumns?.map(c => {
            return { field: c.field, width: c.width ?? 80 };
          }) ?? []
        );
        props.onPresetApplied?.(null);
      }
      savedFiltersPopoverRef.current?.hide();
    };

    const handleSavePreset = (name: string) => {
      if (!locationKey) return;
      const newPreset: SavedFilterPreset = {
        id: crypto.randomUUID(),
        name,
        filters,
        logicalOperator,
        visibleFields: visibleFields.map(f => f.field)
      };
      const updatedMap = { ...savedFiltersMap };
      updatedMap[locationKey] = [...presetsForTable, newPreset];
      updateUserSavedFilters(JSON.stringify(updatedMap));
      
      setActivePresetId(newPreset.id);
      localStorage.setItem(`lastPreset_${locationKey}`, newPreset.id);
    };

    const handleDeletePreset = (presetId: string) => {
      if (!locationKey) return;
      const updatedMap = { ...savedFiltersMap };
      updatedMap[locationKey] = presetsForTable.filter(p => p.id !== presetId);
      updateUserSavedFilters(JSON.stringify(updatedMap));
      if (activePresetId === presetId) {
        setActivePresetId('');
        localStorage.removeItem(`lastPreset_${locationKey}`);
        setFilters(props.initialFilters?.filters ?? []);
        setLogicalOperator(props.initialFilters?.logicalOperator ?? LogicalOperator.AND);
        setVisibleFields(
          props.initialColumns?.map(c => {
            return { field: c.field, width: c.width ?? 80 };
          }) ?? []
        );
        props.onPresetApplied?.(null);
      }
    };

    const handleUpdatePreset = () => {
      if (!locationKey || !activePresetId) return;
      const updatedMap = { ...savedFiltersMap };
      const tablePresets = [...presetsForTable];
      const idx = tablePresets.findIndex(p => p.id === activePresetId);
      if (idx > -1) {
        tablePresets[idx] = {
          ...tablePresets[idx],
          filters,
          logicalOperator,
          visibleFields: visibleFields.map(f => f.field)
        };
        updatedMap[locationKey] = tablePresets;
        updateUserSavedFilters(JSON.stringify(updatedMap));
      }
      savedFiltersPopoverRef.current?.hide();
    };

    const handleColumnChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>, column: DataTableColumn) => {
        if (e.target.checked) {
          setVisibleFields([...visibleFields, { field: column.field, width: column.width }]);
          return;
        }

        const i = visibleFields.findIndex(f => f.field === column.field);
        if (i > -1) {
          const newColumns = [...visibleFields];
          newColumns.splice(i, 1);
          setVisibleFields(newColumns);
        }
      },
      [visibleFields]
    );

    // End Column Helper Methods

    return (
      <>
        <div
          className={
            props.hideTableControls
              ? 'd-flex mb-2 justify-content-end'
              : 'd-flex mb-2 justify-content-between table-controls'
          }
        >
          {props.hideTableControls ?? (
            <div className="d-flex gap-3">
              <Popover
                triggerClassNames="link d-flex align-items-center p-relative"
                trigger={
                  <>
                    <span className="material-icons">view_week</span>
                    Columns
                  </>
                }
                content={
                  showColumnsCategorized ? (
                    <ColumnsControl
                      columns={columns}
                      visibleFields={visibleFields}
                      categories={props.columnCategories}
                      showAllCategories={showAllCategories}
                      setShowAllCategories={setShowAllCategories}
                      handleColumnChange={handleColumnChange}
                    />
                  ) : (
                    // here for legacy support (tables without categorized columns)
                    <>
                      <div className="d-flex flex-column mb-3">
                        <div className="d-flex justify-content-between">
                          <h4>Columns</h4>
                        </div>
                        <p>Please toggle the switches to select specific columns for display in the table.</p>
                        <div className="d-flex justify-content-between mt-3">
                          <div className="search">
                            <input
                              id="columnSearch"
                              onChange={e => setColumnSearch(e.target.value)}
                              value={columnSearch}
                              placeholder="Search"
                              className="input-field"
                            />
                            <span className="material-icons">search</span>
                          </div>
                          <div className="d-flex g-3">
                            <button className="btn-outline" onClick={() => setVisibleFields([])}>
                              HIDE ALL
                            </button>
                            <button
                              className="btn-outline"
                              onClick={() =>
                                setVisibleFields(
                                  columns.map(c => {
                                    return { field: c.field, width: c.width };
                                  })
                                )
                              }
                            >
                              SHOW ALL
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="cc-2">
                        {columns
                          .filter(c => !c.hidden)
                          .filter(c => c.label.toLowerCase().includes(columnSearch.toLowerCase()))
                          .map(c => (
                            <div key={c.field} className="d-flex align-items-center mb-2">
                              <label className="switch">
                                <input
                                  type="checkbox"
                                  checked={visibleFields.includes({ field: c.field, width: c.width })}
                                  onChange={e => {
                                    if (e.target.checked) {
                                      setVisibleFields([...visibleFields, { field: c.field, width: c.width }]);
                                    } else {
                                      const i = visibleFields.indexOf({ field: c.field, width: c.width });
                                      if (i > -1) {
                                        const newColumns = [...visibleFields];
                                        newColumns.splice(i, 1);
                                        setVisibleFields(newColumns);
                                      } else {
                                        // do nothing
                                      }
                                    }
                                  }}
                                />
                                <span className="slider round" />
                              </label>
                              <span style={{ marginLeft: '8px' }}>{c.label}</span> {/* Adjust margin-left as needed */}
                            </div>
                          ))}
                      </div>
                    </>
                  )
                }
              ></Popover>
              <Popover
                data-cy="data-table-filters"
                triggerClassNames="link d-flex align-items-center p-relative"
                trigger={
                  <>
                    <span className="material-icons mr-2">filter_alt</span>
                    {filters.length > 0 && <span className="badge">{filters.length}</span>}
                    Filters
                  </>
                }
                content={
                  <FiltersControl
                    filters={filters}
                    columns={columns}
                    handleAddFilter={handleAddFilter}
                    handleRemoveFilter={handleRemoveFilter}
                    handleFilterUpdate={handleFilterUpdate}
                    logicalOperator={logicalOperator}
                    setLogicalOperator={setLogicalOperator}
                    getFilterOperators={getFilterOperators}
                    getFilterValueComponent={getFilterValueComponent}
                  />
                }
              ></Popover>
              <button disabled={loadingCSV} onClick={handleExportToCSV} className="link d-flex align-items-center">
                <span className="material-icons">download</span>
                Export
                {/* TODO: replace <CircularProgress> ? */}
                {loadingCSV && <CircularProgress />}
              </button>
              {!!props.enableSaveColumns && (
                <Popover
                  ref={savedFiltersPopoverRef}
                  triggerClassNames="link d-flex align-items-center p-relative"
                  trigger={
                    <>
                      <span className="material-icons me-1">bookmark</span>
                      {activePresetId && presetsForTable.find(p => p.id === activePresetId)
                        ? presetsForTable.find(p => p.id === activePresetId)?.name
                        : 'SAVE/LOAD VIEW'}
                    </>
                  }
                  content={
                    <div className="d-flex flex-column p-2" style={{ minWidth: '150px' }}>
                      <h4 className="mb-3 text-uppercase">SAVE/LOAD VIEW</h4>
                      {presetsForTable.length === 0 ? (
                        <div className="text-muted mb-2">No saved views</div>
                      ) : (
                        <div className="d-flex flex-column" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {[...presetsForTable]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(p => (
                              <button
                                key={p.id}
                                className={`${p.id === activePresetId ? 'btn-primary' : 'btn-outline'} text-start w-100 mb-2`}
                                onClick={() => handleApplyPreset(p.id)}
                                style={{ padding: '6px 12px' }}
                              >
                                {p.name}
                              </button>
                            ))}
                        </div>
                      )}
                      {activePresetId ? (
                        <div className="d-flex flex-column mt-2 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                          <button
                            className="preset-action-btn w-100 mb-2"
                            onClick={handleUpdatePreset}
                          >
                            Update Current
                          </button>
                          <button
                            className="preset-action-btn w-100 mb-2"
                            onClick={() => saveFilterDialog.openDialog()}
                          >
                            Save As New
                          </button>
                           <button
                            className="preset-action-btn preset-delete w-100 mb-2"
                            onClick={() => deleteFilterDialog.openDialog()}
                          >
                            Delete Current
                          </button>
                          <button
                            className="preset-action-btn w-100"
                            onClick={() => handleApplyPreset('')}
                          >
                            Clear Selection
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex flex-column mt-2 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                          <button
                            className="preset-action-btn w-100"
                            onClick={() => saveFilterDialog.openDialog()}
                          >
                            Save New View
                          </button>
                        </div>
                      )}
                    </div>
                  }
                ></Popover>
              )}
            </div>
          )}

          <div className="d-flex align-items-center g-3">
            {props.searchAdornment}
            <div className="search">
              <input
                type="text"
                placeholder="Search"
                value={fetchParams.quickSearch ?? ''}
                onChange={event => setFetchParams({ ...fetchParams, quickSearch: event.target.value })}
                className="input-field"
                data-cy="data-table-search"
              />
              <div className="material-icons">
                <span className="left">search</span>
                <span
                  className="right"
                  hidden={!fetchParams.quickSearch}
                  onClick={() => setFetchParams({ ...fetchParams, quickSearch: '' })}
                >
                  close
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={`table-container ${props.tableClass ?? ''}`}>
          {!!props.enableSaveColumns && (
            <>
              <SaveFilterDialog dialog={saveFilterDialog} onSave={handleSavePreset} />
              <ConfirmDeletePresetDialog
                dialog={deleteFilterDialog}
                presetName={activePresetId ? presetsForTable.find(p => p.id === activePresetId)?.name || '' : ''}
                onConfirm={() => handleDeletePreset(activePresetId)}
              />
            </>
          )}
          <table
            className={`custom-table ${loading ? 'table-loading' : ''}`}
            onDragOver={allowDrop}
            onDrop={handleColumnDrop}
            data-cy={props['data-cy']}
          >
            <thead className="table-header text-uppercase">
              <tr>
                {props.checkboxSelection && (
                  <td className={'column-label'} width={50} key={'checkbox-header'}>
                    <div className="header-content">
                      <input
                        type="checkbox"
                        className="checkbox pointer"
                        checked={data.length === selectedRows.length}
                        onChange={event => {
                          setSelectedRows(event.target.checked ? Array.from(Array(data.length).keys()) : []);
                          if (!!props.onRowSelectionModelChange) {
                            props?.onRowSelectionModelChange(
                              (event.target.checked ? Array.from(Array(data.length).keys()) : []).map(idx => data[idx])
                            );
                          }
                        }}
                        data-cy="data-table-header-checkbox"
                      />
                      <span className="column-resize"></span>
                    </div>
                  </td>
                )}
                {visibleColumns
                  .filter(c => !c.hidden)
                  .map(c => (
                    <td className={c.headerClass + ' column-label'} width={c.width ?? 80} key={c.field}>
                      <div className="header-content">
                        <div
                          className={`header-content ${c.sortable === false ? '' : 'cursor-pointer'}`}
                          onClick={c.sortable === false ? undefined : handleSort(c.sortField || c.field)}
                          onDragStart={handleColumnDrag(c)}
                          draggable
                        >
                          <span className="material-icons cursor-grab drag-handle">drag_indicator</span>
                          {c.label}
                          {(!!c.type?.includes('date') || c.type === 'zoned') && <>&nbsp;({timeZoneIndicator})</>}
                          {fetchParams.sortField === (c.sortField || c.field) && (
                            <div className="material-icons cursor-pointer">
                              {fetchParams.sortDirection === 'desc' ? 'expand_more' : 'expand_less'}
                            </div>
                          )}
                        </div>
                        <span className="column-resize" onMouseDown={handleColumnResize(c)}></span>
                      </div>
                    </td>
                  ))}
                {props?.getRowActions && <td className="row-actions" />}
              </tr>
            </thead>
            <tbody>
              {getData().map((r, i) => (
                <tr
                  className={props.rowClassName?.(r)}
                  key={i}
                  onClick={() => props.onRowClick?.(r)}
                  data-cy={`${props['data-cy'] ? `${props['data-cy']}-` : ''}data-table-row-${r.id}`}
                >
                  {props.checkboxSelection && (
                    <td>
                      <input
                        type="checkbox"
                        key={selectedRows.length + '' + i}
                        defaultChecked={selectedRows.includes(i)}
                        onClick={event => {
                          handleUpdateRowSelection(i)(event as any);
                          event.stopPropagation();
                          event.preventDefault();
                        }}
                      />
                    </td>
                  )}
                  {visibleColumns
                    .filter(c => !c.hidden)
                    .map(c => {
                      const tdRef = React.createRef<HTMLTableDataCellElement>();
                      const tdClass = (!c.classCondition || c.classCondition(getColumnValue(r, c), r)) && c.className;
                      return (
                        <td
                          className={`${tdClass || ''} wrap`}
                          width={c.width ?? 80}
                          onClick={() => props.onCellClick?.(c.field, getColumnValue(r, c), r)}
                          key={`${c.field}-${i}`}
                          id={`${c.field}-${i}-td`}
                          ref={tdRef}
                          data-column={c.field}
                        >
                          {c.type?.includes('date') ? (
                            !getColumnValue(r, c) ? null : (
                              <span className="overflow" id={`${c.field}-${i}`}>
                                {newZonedDayJs(getColumnValue(r, c)).format(c.dateFormat ?? 'M/D/YY LT')}
                                {tdClass === 'backdated' && '**'}
                              </span>
                            )
                          ) : !!c.renderCell ? (
                            c.renderCell(getColumnValue(r, c), r)
                          ) : (
                            <span className="overflow" id={`${c.field}-${i}`}>
                              {getColumnValueLabel(r, c)}
                            </span>
                          )}
                          {/* {(document.getElementById(`${c.field}-${i}`)?.clientWidth ?? 0) < */}
                          {/* (document.getElementById(`${c.field}-${i}`)?.scrollWidth ?? 0) && ( */}
                          <span className="tooltip">{getColumnValueLabel(r, c)}</span>
                        </td>
                      );
                    })}
                  {props?.getRowActions && (
                    <td className="row-actions">
                      {props.getRowActions(r).map((ele, i) => (
                        <div key={i}>{ele}</div>
                      ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {/* TODO: replace <CircularProgress> ? */}
          {loading && (
            <div className="circular-progress-overlay">
              <CircularProgress size={70} color="primary" />
            </div>
          )}
          {!loading && data.length === 0 && (
            <div className="no-rows-message p-4">{props.noRowsMessage ?? <span>No data to display in table</span>}</div>
          )}
        </div>
        {!!totalCount && (
          <>
            <div className="pagination-container">
              Rows per page:
              <select
                value={fetchParams.pageSize}
                onChange={event => {
                  setFetchParams({ ...fetchParams, pageSize: +event.target.value, pageNum: 0 });
                  setSelectedRows([]);
                }}
              >
                {(props.pageSizeOptions ?? [25, 50, 100]).map(num => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              {fetchParams?.pageNum !== undefined && fetchParams.pageSize !== undefined && (
                <>
                  {`${fetchParams.pageNum * fetchParams.pageSize + 1}-
                  ${fetchParams.pageNum * fetchParams.pageSize + getData().length} of ${
                    props.clientSidePagination ? filterData(data).length : totalCount
                  }`}
                  <button
                    className="pagination-button"
                    disabled={fetchParams.pageNum === 0}
                    onClick={() => {
                      fetchParams.pageNum !== undefined &&
                        setFetchParams({ ...fetchParams, pageNum: fetchParams.pageNum - 1 });
                      setSelectedRows([]);
                    }}
                  >
                    {''}
                    <span className="material-icons">chevron_left</span>
                  </button>
                  <button
                    className="pagination-button"
                    disabled={(fetchParams.pageNum + 1) * fetchParams.pageSize >= totalCount}
                    onClick={() =>
                      fetchParams.pageNum !== undefined &&
                      setFetchParams({ ...fetchParams, pageNum: fetchParams.pageNum + 1 })
                    }
                  >
                    {''}
                    <span className="material-icons">chevron_right</span>
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </>
    );
  }
);

export default DataTable;
